import React from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  Calculator,
  TrendingUp,
  ClipboardCheck,
  FileSpreadsheet,
  Users,
  ArrowRight,
  Linkedin,
  Mail,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import usePageSeo from '../hooks/usePageSeo';

const FIRM_LINKEDIN = 'https://www.linkedin.com/company/wpaauae';
const FIRM_EMAIL = 'info@wpaauae.com';

// Leadership cards — 2×2 layout on desktop.
const leadershipRoles = [
  {
    key: 'managing-partner',
    role: 'Managing Partner',
    icon: Award,
    accent: 'from-teal-500 to-teal-700',
    accentSoft: 'bg-teal-50 text-teal-700 border-teal-100',
    responsibilityLabel: 'Leadership Responsibility',
    responsibility:
      'Provides strategic leadership to WPAA, oversees professional standards and quality, and remains accountable for major client engagements.',
    experience: '20+ years in audit, assurance, governance and financial reporting across Middle East markets.',
    engagementLabel: 'Engagement Role',
    engagement: [
      'Firm leadership and strategy',
      'Audit quality oversight',
      'Key client relationships',
      'Complex engagement review'
    ],
    qualifications: ['CPA', 'CA', 'ACCA']
  },
  {
    key: 'tax-advisory-partner',
    role: 'Tax Advisory Partner',
    icon: Calculator,
    accent: 'from-emerald-500 to-emerald-700',
    accentSoft: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    responsibilityLabel: 'Leadership Responsibility',
    responsibility:
      "Leads WPAA's tax practice and reviews significant UAE Corporate Tax, VAT and transfer pricing matters.",
    experience: '15+ years in taxation, compliance and business advisory.',
    engagementLabel: 'Engagement Role',
    engagement: [
      'Corporate Tax strategy',
      'VAT advisory and compliance',
      'Transfer pricing matters',
      'FTA submissions and technical review'
    ],
    qualifications: ['CTA', 'ACCA', 'MBA']
  },
  {
    key: 'advisory-services-director',
    role: 'Advisory Services Director',
    icon: TrendingUp,
    accent: 'from-cyan-500 to-cyan-700',
    accentSoft: 'bg-cyan-50 text-cyan-700 border-cyan-100',
    responsibilityLabel: 'Leadership Responsibility',
    responsibility:
      'Leads financial and strategic advisory assignments, helping clients evaluate risks, transactions and business decisions.',
    experience: null,
    engagementLabel: 'Engagement Role',
    engagement: [
      'Due diligence',
      'Business valuation',
      'Financial restructuring',
      'Strategic and transaction advisory'
    ],
    qualifications: ['MBA', 'CFA', 'CIA']
  },
  {
    key: 'senior-audit-manager',
    role: 'Senior Audit Manager',
    icon: ClipboardCheck,
    accent: 'from-sky-500 to-sky-700',
    accentSoft: 'bg-sky-50 text-sky-700 border-sky-100',
    responsibilityLabel: 'Leadership Responsibility',
    responsibility:
      'Manages audit planning, fieldwork, team coordination and reporting while supporting compliance with applicable auditing and financial reporting standards.',
    experience: null,
    engagementLabel: 'Engagement Role',
    engagement: [
      'Audit planning and execution',
      'Financial statement review',
      'Engagement supervision',
      'Regulatory compliance'
    ],
    qualifications: ['CA', 'ACCA']
  }
];

// Delivery & Client Support — 2 cards side by side.
const deliveryTeams = [
  {
    key: 'accounting-reporting-team',
    role: 'Accounting & Reporting Team',
    icon: FileSpreadsheet,
    accent: 'from-blue-500 to-blue-700',
    accentSoft: 'bg-blue-50 text-blue-700 border-blue-100',
    responsibilityLabel: 'Role',
    responsibility:
      'Supports the accurate and timely maintenance of client financial records, reconciliations, management reporting and financial statement preparation.',
    experience: null,
    engagementLabel: 'Key Responsibilities',
    engagement: [
      'Bookkeeping and ledger review',
      'Bank and account reconciliations',
      'Monthly management reporting',
      'Financial statement preparation',
      'Audit schedules and supporting documents'
    ]
  },
  {
    key: 'client-support-operations-team',
    role: 'Client Support & Operations Team',
    icon: Users,
    accent: 'from-slate-500 to-slate-700',
    accentSoft: 'bg-slate-50 text-slate-700 border-slate-100',
    responsibilityLabel: 'Role',
    responsibility:
      'Coordinates client onboarding, documentation, communication and engagement administration to keep assignments organised and on schedule.',
    experience: null,
    engagementLabel: 'Key Responsibilities',
    engagement: [
      'Client onboarding and KYC coordination',
      'Document collection and organisation',
      'Engagement scheduling and tracking',
      'Deadline reminders and status updates',
      'General administrative support'
    ]
  }
];

// Shared card renderer.
const TeamCard = ({ r }) => {
  const Icon = r.icon;
  return (
    <Card
      className="border border-gray-200 hover:border-teal-500 hover:shadow-xl transition-all flex flex-col overflow-hidden"
      data-testid={`team-card-${r.key}`}
    >
      {/* Accent stripe */}
      <div className={`h-1.5 bg-gradient-to-r ${r.accent}`} />

      <CardContent className="p-6 flex flex-col flex-1">
        {/* Icon + role header */}
        <div className="flex items-start gap-3 mb-5">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${r.accent} flex items-center justify-center flex-shrink-0 shadow-sm`}>
            <Icon size={22} className="text-white" strokeWidth={2} />
          </div>
          <div className="pt-0.5">
            <h3 className="text-lg lg:text-xl font-bold text-gray-900 leading-snug">
              {r.role}
            </h3>
          </div>
        </div>

        {/* Responsibility */}
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
            {r.responsibilityLabel}
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            {r.responsibility}
          </p>
        </div>

        {/* Experience (optional) */}
        {r.experience && (
          <div className="mb-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
              Experience
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              {r.experience}
            </p>
          </div>
        )}

        {/* Engagement / Key Responsibilities */}
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
            {r.engagementLabel}
          </p>
          <ul className="space-y-1.5">
            {r.engagement.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle2 size={14} className="text-teal-600 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Qualifications OR Systems Experience (mutually exclusive; optional) */}
        {(r.qualifications || r.systemsExperience) && (
          <div className="mt-auto pt-4 border-t border-gray-100">
            {r.qualifications && (
              <>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                  Qualifications
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {r.qualifications.map((q) => (
                    <span
                      key={q}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${r.accentSoft}`}
                    >
                      {q}
                    </span>
                  ))}
                </div>
              </>
            )}
            {r.systemsExperience && (
              <>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                  {r.systemsLabel || 'Systems Experience'}
                </p>
                <p className={`text-sm font-medium ${r.accentSoft ? r.accentSoft.split(' ')[1] : 'text-gray-800'}`}>
                  {r.systemsExperience}
                </p>
              </>
            )}
          </div>
        )}

        {/* Firm-level contact actions */}
        <div className="mt-5 pt-4 border-t border-gray-100 flex gap-2">
          <a
            href={FIRM_LINKEDIN}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
            data-testid={`team-card-linkedin-${r.key}`}
          >
            <button className="w-full flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-teal-600 hover:text-white text-gray-700 py-2 px-3 rounded-lg transition-colors text-xs font-semibold">
              <Linkedin size={14} />
              <span>Connect with WPAA</span>
            </button>
          </a>
          <a
            href={`mailto:${FIRM_EMAIL}`}
            className="flex-1"
            data-testid={`team-card-email-${r.key}`}
          >
            <button className="w-full flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-teal-600 hover:text-white text-gray-700 py-2 px-3 rounded-lg transition-colors text-xs font-semibold">
              <Mail size={14} />
              <span>Contact Our Team</span>
            </button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const Team = () => {
  usePageSeo({
    title: 'Our Leadership & Team | WPAA Audit, Tax & Advisory UAE',
    description:
      'Experienced leadership plus a coordinated Accounting & Reporting and Client Support & Operations team delivering audit, tax and advisory engagements at WPAA.',
    path: '/team'
  });

  return (
    <div className="min-h-screen" data-testid="team-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-16 left-10 w-64 h-64 bg-teal-200 rounded-full filter blur-3xl" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-emerald-200 rounded-full filter blur-3xl" />
        </div>
        <div className="container mx-auto text-center relative z-10 max-w-4xl">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider" data-testid="team-hero-eyebrow">
            Our Leadership &amp; Team
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mt-4 mb-5 leading-tight" data-testid="team-hero-title">
            The Experience and Leadership Behind WPAA
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-testid="team-hero-subtitle">
            Our firm is supported by experienced professionals who lead client relationships, technical review and engagement delivery across audit, tax and advisory assignments.
          </p>
        </div>
      </section>

      {/* Leadership — 2x2 */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6" data-testid="team-leadership-grid">
            {leadershipRoles.map((r) => (
              <TeamCard key={r.key} r={r} />
            ))}
          </div>
        </div>
      </section>

      {/* Delivery & Client Support — 2 cards side by side */}
      <section className="pb-16 md:pb-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider" data-testid="delivery-section-eyebrow">
              Delivery &amp; Client Support
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Coordinated Delivery Across Every Engagement
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-3">
              Behind every WPAA engagement is a team focused on accurate reporting, clean documentation and smooth day-to-day coordination.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6" data-testid="team-delivery-grid">
            {deliveryTeams.map((r) => (
              <TeamCard key={r.key} r={r} />
            ))}
          </div>

          {/* Central primary CTA */}
          <div className="mt-14 flex justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-base shadow-md hover:shadow-lg transition-all"
                data-testid="team-speak-cta"
              >
                Speak With Our Team
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Collective Strength */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Our Collective Strength</h2>
            <p className="text-teal-100 text-lg max-w-2xl mx-auto">
              Combined expertise across audit, tax, and advisory that drives dependable results for our clients.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { value: '18+', label: 'Years Combined Experience' },
              { value: '20+', label: 'Professional Certifications' },
              { value: '200+', label: 'Clients Successfully Served' },
              { value: '100%', label: 'Client Satisfaction Rate' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 md:p-8 hover:bg-white/20 transition-all">
                  <p className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</p>
                  <p className="text-teal-100 text-sm md:text-base">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers CTA */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-gray-50 to-teal-50 rounded-2xl p-10 md:p-12 text-center border-2 border-teal-200">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Join Our Growing Team</h2>
            <p className="text-gray-600 text-base md:text-lg mb-7">
              We&apos;re always looking for talented professionals who share our commitment to excellence and integrity. Explore career opportunities at White Paper.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/careers">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8" data-testid="team-careers-open-positions">
                  View Open Positions
                </Button>
              </Link>
              <a href="mailto:careers@wpaauae.com" data-testid="team-careers-send-cv">
                <Button size="lg" variant="outline" className="border-2 border-gray-300 hover:border-teal-600 hover:text-teal-600 px-8">
                  Send Your CV
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
